import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { resolveTemplatePreview } from './helpers';
import { API_BASE_URL } from '../config';

// ---------------------------------------------------------------------------
// helpers.* catalog — mirrors src/backend/engine/helpers.py
// ---------------------------------------------------------------------------

const HELPERS_CATALOG = [
  { category: 'String', fns: [
    { name: 'lower',         sig: 'helpers.lower(s)',                desc: 'Lowercase string' },
    { name: 'upper',         sig: 'helpers.upper(s)',                desc: 'Uppercase string' },
    { name: 'trim',          sig: 'helpers.trim(s)',                 desc: 'Strip whitespace' },
    { name: 'replace',       sig: 'helpers.replace(s, old, new)',    desc: 'Replace substring' },
    { name: 'split',         sig: 'helpers.split(s, sep)',           desc: 'Split to list' },
    { name: 'join',          sig: 'helpers.join(list, sep)',         desc: 'Join list to string' },
    { name: 'contains',      sig: 'helpers.contains(s, sub)',        desc: 'Check if contains' },
    { name: 'starts_with',   sig: 'helpers.starts_with(s, prefix)',  desc: 'Check prefix' },
    { name: 'ends_with',     sig: 'helpers.ends_with(s, suffix)',    desc: 'Check suffix' },
    { name: 'substring',     sig: 'helpers.substring(s, start, end)',desc: 'Extract substring' },
    { name: 'pad_left',      sig: 'helpers.pad_left(s, width)',      desc: 'Left-pad string' },
    { name: 'pad_right',     sig: 'helpers.pad_right(s, width)',     desc: 'Right-pad string' },
    { name: 'md5',           sig: 'helpers.md5(s)',                  desc: 'MD5 hash' },
    { name: 'base64_encode', sig: 'helpers.base64_encode(s)',        desc: 'Base64 encode' },
    { name: 'base64_decode', sig: 'helpers.base64_decode(s)',        desc: 'Base64 decode' },
    { name: 'url_encode',    sig: 'helpers.url_encode(s)',           desc: 'URL encode' },
    { name: 'url_decode',    sig: 'helpers.url_decode(s)',           desc: 'URL decode' },
  ]},
  { category: 'Number', fns: [
    { name: 'round',         sig: 'helpers.round(n, dp)',            desc: 'Round to decimal places' },
    { name: 'abs',           sig: 'helpers.abs(n)',                  desc: 'Absolute value' },
    { name: 'ceil',          sig: 'helpers.ceil(n)',                 desc: 'Round up (ceiling)' },
    { name: 'floor',         sig: 'helpers.floor(n)',                desc: 'Round down (floor)' },
    { name: 'min',           sig: 'helpers.min(list)',               desc: 'Minimum value' },
    { name: 'max',           sig: 'helpers.max(list)',               desc: 'Maximum value' },
    { name: 'sum',           sig: 'helpers.sum(list)',               desc: 'Sum of list' },
    { name: 'avg',           sig: 'helpers.avg(list)',               desc: 'Average of list' },
    { name: 'format_number', sig: 'helpers.format_number(n, fmt)',   desc: 'Format number (e.g. ",.2f")' },
  ]},
  { category: 'Date', fns: [
    { name: 'now',           sig: 'helpers.now()',                       desc: 'Current UTC datetime' },
    { name: 'today',         sig: 'helpers.today()',                     desc: "Today's date" },
    { name: 'add_days',      sig: 'helpers.add_days(dt, n)',             desc: 'Add N days' },
    { name: 'add_hours',     sig: 'helpers.add_hours(dt, n)',            desc: 'Add N hours' },
    { name: 'add_minutes',   sig: 'helpers.add_minutes(dt, n)',          desc: 'Add N minutes' },
    { name: 'format_date',   sig: 'helpers.format_date(dt, fmt)',        desc: 'Format date (strftime)' },
    { name: 'parse_date',    sig: 'helpers.parse_date(s, fmt)',          desc: 'Parse date string' },
    { name: 'diff_days',     sig: 'helpers.diff_days(a, b)',             desc: 'Days between dates' },
    { name: 'timestamp',     sig: 'helpers.timestamp(dt)',               desc: 'Unix timestamp' },
  ]},
  { category: 'Array', fns: [
    { name: 'first',   sig: 'helpers.first(list)',               desc: 'First element' },
    { name: 'last',    sig: 'helpers.last(list)',                desc: 'Last element' },
    { name: 'length',  sig: 'helpers.length(list)',              desc: 'Length of list or string' },
    { name: 'slice',   sig: 'helpers.slice(list, start, end)',   desc: 'Slice list or string' },
    { name: 'filter',  sig: 'helpers.filter(list, key, value)',  desc: 'Filter by key=value' },
    { name: 'map',     sig: 'helpers.map(list, key)',            desc: 'Extract key from each item' },
    { name: 'unique',  sig: 'helpers.unique(list, key)',         desc: 'Deduplicate by key' },
    { name: 'flatten', sig: 'helpers.flatten(list)',             desc: 'Flatten one level' },
    { name: 'sort',    sig: 'helpers.sort(list, key, desc)',     desc: 'Sort list' },
    { name: 'count',   sig: 'helpers.count(list, value)',        desc: 'Count occurrences' },
  ]},
  { category: 'JSON', fns: [
    { name: 'get',    sig: "helpers.get(obj, 'dot.path', default)", desc: 'Safe nested get' },
    { name: 'keys',   sig: 'helpers.keys(obj)',                     desc: 'Dict keys' },
    { name: 'values', sig: 'helpers.values(obj)',                   desc: 'Dict values' },
    { name: 'merge',  sig: 'helpers.merge(a, b)',                   desc: 'Merge two dicts' },
    { name: 'set',    sig: 'helpers.set(obj, key, value)',          desc: 'Set key on dict (returns copy)' },
    { name: 'omit',   sig: 'helpers.omit(obj, key1, key2)',         desc: 'Remove keys from dict' },
    { name: 'pick',   sig: 'helpers.pick(obj, key1, key2)',         desc: 'Keep only these keys' },
  ]},
  { category: 'Conditional', fns: [
    { name: 'if_empty', sig: 'helpers.if_empty(val, fallback)',       desc: 'Fallback if null/empty' },
    { name: 'coalesce', sig: 'helpers.coalesce(val1, val2, ...)',     desc: 'First non-null value' },
    { name: 'if_else',  sig: 'helpers.if_else(condition, yes, no)',   desc: 'Inline conditional' },
  ]},
];

// ---------------------------------------------------------------------------
// Misc
// ---------------------------------------------------------------------------

/** Flatten an object into dot-path entries for field suggestions. */
function flattenOutputFields(obj, prefix = '') {
  if (!obj || typeof obj !== 'object') return [];
  const result = [];
  for (const [k, v] of Object.entries(obj)) {
    const path = prefix ? `${prefix}.${k}` : k;
    if (v !== null && typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length > 0) {
      result.push(...flattenOutputFields(v, path));
    } else {
      const preview =
        v === null ? 'null'
        : typeof v === 'object' ? '(object)'
        : String(v).slice(0, 35);
      result.push({ path, preview });
    }
  }
  return result;
}

/**
 * Scan text up to `cursor` tracking {{ ... }} pairs.
 * Returns the partial expression text if the cursor is inside an open {{ }},
 * or null if not inside an expression.
 */
export function getPartialExpression(text, cursor) {
  let inExpr = false;
  let exprStart = -1;
  let i = 0;
  while (i < cursor) {
    if (!inExpr && i + 1 < text.length && text[i] === '{' && text[i + 1] === '{') {
      inExpr = true;
      exprStart = i + 2;
      i += 2;
    } else if (inExpr && i + 1 < text.length && text[i] === '}' && text[i + 1] === '}') {
      inExpr = false;
      exprStart = -1;
      i += 2;
    } else {
      i++;
    }
  }
  if (!inExpr || exprStart === -1) return null;
  return text.slice(exprStart, cursor).trimStart();
}

/** Returns the index of the {{ that the cursor is currently inside, or -1. */
export function findExpressionStart(text, cursor) {
  let inExpr = false;
  let exprOpenAt = -1;
  let i = 0;
  while (i < cursor) {
    if (!inExpr && i + 1 < text.length && text[i] === '{' && text[i + 1] === '{') {
      inExpr = true;
      exprOpenAt = i;
      i += 2;
    } else if (inExpr && i + 1 < text.length && text[i] === '}' && text[i + 1] === '}') {
      inExpr = false;
      exprOpenAt = -1;
      i += 2;
    } else {
      i++;
    }
  }
  return inExpr ? exprOpenAt : -1;
}

/** Build autocomplete suggestions from the partial expression at cursor. */
export function buildSuggestions(partial, ancestorNodes, nodeStatus, envVars) {
  if (partial === null) return [];
  const trimmed = partial.trim();

  // Empty — show top-level categories
  if (trimmed === '') {
    const topNodes = ancestorNodes.slice(0, 5).map(n => {
      const label = n.data?.label || n.id;
      return { label: `nodes['${label}']`, insert: `nodes['${label}'] }}`, desc: 'Node output' };
    });
    return [
      ...topNodes,
      ...(envVars.length > 0
        ? [{ label: "env['…']", insert: null, desc: `${envVars.length} variable${envVars.length !== 1 ? 's' : ''}`, isCategory: true }]
        : []),
      { label: 'now', insert: 'now }}', desc: 'ISO datetime' },
      { label: 'helpers.*', insert: null, desc: 'string/number/date/array/JSON helpers', isCategory: true },
    ];
  }

  // nodes path ---------------------------------------------------------------
  if (trimmed.startsWith('nodes')) {
    // nodes['NodeName'].field
    const fullFieldMatch = trimmed.match(/^nodes\[['"](.+?)['"]\]\.(.*)$/);
    if (fullFieldMatch) {
      const nodeName = fullFieldMatch[1];
      const fieldPartial = fullFieldMatch[2];
      const nodeObj = ancestorNodes.find(n => (n.data?.label || n.id) === nodeName);
      if (nodeObj && nodeStatus[nodeObj.id]?.output) {
        return flattenOutputFields(nodeStatus[nodeObj.id].output)
          .filter(f => fieldPartial === '' || f.path.toLowerCase().startsWith(fieldPartial.toLowerCase()))
          .slice(0, 12)
          .map(f => ({
            label: `nodes['${nodeName}'].${f.path}`,
            insert: `nodes['${nodeName}'].${f.path} }}`,
            desc: f.preview,
          }));
      }
      return [];
    }

    // nodes['NodeName'] — complete node reference (offer full + field expansions)
    const nodeOnlyMatch = trimmed.match(/^nodes\[['"](.+?)['"]\]$/);
    if (nodeOnlyMatch) {
      const nodeName = nodeOnlyMatch[1];
      const nodeObj = ancestorNodes.find(n => (n.data?.label || n.id) === nodeName);
      const results = [{ label: `nodes['${nodeName}']`, insert: `nodes['${nodeName}'] }}`, desc: 'Full output' }];
      if (nodeObj && nodeStatus[nodeObj.id]?.output) {
        flattenOutputFields(nodeStatus[nodeObj.id].output)
          .slice(0, 8)
          .forEach(f => {
            results.push({
              label: `.${f.path}`,
              insert: `nodes['${nodeName}'].${f.path} }}`,
              desc: f.preview,
            });
          });
      }
      return results;
    }

    // nodes[ — show matching ancestor node labels
    const nodePartialMatch = trimmed.match(/^nodes\[['"]?(.*)$/);
    const labelPartial = nodePartialMatch ? nodePartialMatch[1].replace(/['"]$/, '') : '';
    return ancestorNodes
      .filter(n => {
        const lbl = n.data?.label || n.id;
        return !labelPartial || lbl.toLowerCase().includes(labelPartial.toLowerCase());
      })
      .slice(0, 10)
      .map(n => {
        const lbl = n.data?.label || n.id;
        return { label: `nodes['${lbl}']`, insert: `nodes['${lbl}'] }}`, desc: '' };
      });
  }

  // env path -----------------------------------------------------------------
  if (trimmed.startsWith('env')) {
    const envKeyMatch = trimmed.match(/^env\[['"](.*)$/);
    const keyPartial = envKeyMatch ? envKeyMatch[1].replace(/['"]$/, '') : '';
    return envVars
      .filter(v => !keyPartial || v.key.toLowerCase().includes(keyPartial.toLowerCase()))
      .slice(0, 12)
      .map(v => ({
        label: `env['${v.key}']`,
        insert: `env['${v.key}'] }}`,
        desc: v.is_encrypted ? '🔒 encrypted' : String(v.value || '').slice(0, 30),
      }));
  }

  // now / datetime -----------------------------------------------------------
  if (trimmed.startsWith('now')) {
    return [
      { label: 'now', insert: 'now }}', desc: 'ISO datetime' },
      { label: "now.strftime('%Y-%m-%d')", insert: "now.strftime('%Y-%m-%d') }}", desc: 'Date only' },
      { label: "now.strftime('%H:%M:%S')", insert: "now.strftime('%H:%M:%S') }}", desc: 'Time only' },
    ].filter(s => s.label.toLowerCase().startsWith(trimmed.toLowerCase()));
  }

  // helpers.* ----------------------------------------------------------------
  if (trimmed.startsWith('helpers')) {
    const afterHelpers = trimmed.slice('helpers'.length);

    // Just "helpers" — show category overview
    if (afterHelpers === '') {
      return HELPERS_CATALOG.map(cat => ({
        label: `helpers.  [${cat.category}]`,
        insert: null,
        desc: cat.fns.slice(0, 3).map(f => f.name).join(', ') + '…',
        isCategory: true,
      }));
    }

    // "helpers." — show all functions
    if (afterHelpers.startsWith('.')) {
      const fnPartial = afterHelpers.slice(1).toLowerCase();
      const allFns = HELPERS_CATALOG.flatMap(cat =>
        cat.fns.map(f => ({ ...f, category: cat.category }))
      );
      return allFns
        .filter(f => !fnPartial || f.name.startsWith(fnPartial))
        .map(f => ({
          label: f.sig,
          insert: `${f.sig} }}`,
          desc: `[${f.category}] ${f.desc}`,
        }));
    }
  }

  return [];
}

// ---------------------------------------------------------------------------
// Helper category section (used inside FieldPickerPanel)
// ---------------------------------------------------------------------------

function HelperCategorySection({ cat, expanded, toggle, rowStyle, onInsert }) {
  const key = `helpers-${cat.category}`;
  const isOpen = expanded[key] !== false; // default open
  return (
    <div style={{ marginBottom: '3px' }}>
      <div
        onClick={() => toggle(key)}
        style={{
          padding: '4px 6px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          borderRadius: '4px',
          background: 'var(--bg-tertiary)',
          userSelect: 'none',
        }}
      >
        <span style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>{isOpen ? '▼' : '▶'}</span>
        <span style={{ fontSize: '11px', fontWeight: 600, flex: 1, color: 'var(--text-secondary)' }}>{cat.category}</span>
        <span style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>{cat.fns.length}</span>
      </div>
      {isOpen && (
        <div style={{ paddingLeft: '10px' }}>
          {cat.fns.map(fn => (
            <div
              key={fn.name}
              onMouseDown={e => { e.preventDefault(); onInsert(`{{ ${fn.sig} }}`); }}
              style={rowStyle}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.1)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <span style={{ fontFamily: 'monospace', fontSize: '11px', color: 'var(--accent-color)', flex: 1 }}>{fn.sig.replace('helpers.', '')}</span>
              <span style={{
                fontSize: '10px',
                color: 'var(--text-tertiary)',
                maxWidth: '90px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>{fn.desc}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Field picker panel
// ---------------------------------------------------------------------------

export function FieldPickerPanel({ ancestorNodes, nodeStatus, envVars, onInsert, fieldLabel = '', onClose }) {
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});
  const [nlDescription, setNlDescription] = useState('');
  const [nlResolving, setNlResolving] = useState(false);
  const [nlResult, setNlResult] = useState(null);
  const [nlError, setNlError] = useState('');
  const [nlCredentialId, setNlCredentialId] = useState('');
  const [nlCredentials, setNlCredentials] = useState([]);
  const [showNL, setShowNL] = useState(false);
  const toggle = key => setExpanded(p => ({ ...p, [key]: !p[key] }));
  const searchLow = search.toLowerCase();
  const fieldLabelLow = (fieldLabel || '').toLowerCase().replace(/[^a-z0-9]/g, '');

  // Load AI credentials for NL resolver
  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${API_BASE_URL}/credentials/`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        const aiCreds = (Array.isArray(data) ? data : []).filter(c =>
          ['openai', 'gemini'].includes((c.service_type || '').toLowerCase())
        );
        setNlCredentials(aiCreds);
        if (aiCreds.length === 1) setNlCredentialId(String(aiCreds[0].id));
      })
      .catch(() => {});
  }, []);

  const nlUpstreamFields = useMemo(() => {
    const fields = [];
    for (const node of ancestorNodes) {
      const lbl = node.data?.label || node.id;
      const output = nodeStatus[node.id]?.output;
      if (!output) continue;
      for (const { path, preview } of flattenOutputFields(output)) {
        fields.push({ expression: `nodes['${lbl}'].${path}`, preview });
      }
    }
    for (const v of envVars) {
      fields.push({ expression: `env['${v.key}']`, preview: v.is_encrypted ? '(encrypted)' : String(v.value || '') });
    }
    return fields;
  }, [ancestorNodes, nodeStatus, envVars]);

  const resolveNL = async () => {
    if (!nlDescription.trim()) return;
    if (!nlCredentialId) { setNlError('Select an AI credential first.'); return; }
    setNlResolving(true);
    setNlError('');
    setNlResult(null);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
      const res = await fetch(`${API_BASE_URL}/ai/resolve-expression`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ credential_id: nlCredentialId, description: nlDescription, field_label: fieldLabel, upstream_fields: nlUpstreamFields }),
      });
      const data = await res.json();
      if (!res.ok) { setNlError(data.detail || 'Failed'); return; }
      setNlResult(data.expression || '');
    } catch (e) {
      setNlError(String(e));
    } finally {
      setNlResolving(false);
    }
  };

  /** Score a field path by similarity to the current field label. */
  const scoreField = (path) => {
    if (!fieldLabelLow) return 0;
    const pathLow = path.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (pathLow === fieldLabelLow) return 3;
    if (pathLow.includes(fieldLabelLow)) return 2;
    if (fieldLabelLow.includes(pathLow)) return 1;
    return 0;
  };

  const rowStyle = {
    padding: '3px 6px',
    cursor: 'pointer',
    borderRadius: '3px',
    display: 'flex',
    gap: '6px',
    alignItems: 'center',
  };

  return (
    <div style={{
      position: 'absolute',
      top: 'calc(100% + 2px)',
      right: 0,
      width: 'min(300px, 100%)',
      background: 'var(--bg-secondary)',
      border: '1px solid var(--accent-color)',
      borderRadius: '6px',
      zIndex: 1100,
      maxHeight: '400px',
      overflowY: 'auto',
      overflowX: 'hidden',
      boxShadow: '0 6px 20px rgba(0,0,0,0.4)',
    }}>
      {/* Header + search */}
      <div style={{
        padding: '8px',
        borderBottom: '1px solid var(--border-color)',
        position: 'sticky',
        top: 0,
        background: 'var(--bg-secondary)',
        zIndex: 1,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
          <span style={{
            fontSize: '11px',
            fontWeight: 700,
            color: 'var(--text-tertiary)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}>
            Insert Reference
          </span>
          {onClose && (
            <span
              onMouseDown={e => { e.preventDefault(); onClose(); }}
              style={{ cursor: 'pointer', opacity: 0.6, fontSize: '12px', color: 'var(--text-tertiary)' }}
            >x</span>
          )}
        </div>
        <input
          autoFocus
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search fields, helpers, env..."
          style={{
            width: '100%',
            padding: '5px 8px',
            background: 'var(--bg-input)',
            border: '1px solid var(--border-color)',
            borderRadius: '4px',
            color: 'var(--text-primary)',
            fontSize: '12px',
            boxSizing: 'border-box',
          }}
        />
      </div>

      <div style={{ padding: '6px' }}>
        {/* Upstream nodes */}
        {ancestorNodes.length === 0 && !envVars.length && (
          <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', padding: '8px 6px', fontStyle: 'italic' }}>
            No upstream nodes connected.
          </div>
        )}

        {ancestorNodes.map(node => {
          const label = node.data?.label || node.id;
          const ns = nodeStatus[node.id];
          const rawFields = ns?.output ? flattenOutputFields(ns.output) : [];
          const scored = rawFields.map(f => ({ ...f, score: scoreField(f.path) }));
          scored.sort((a, b) => b.score - a.score);
          const filtered = searchLow
            ? scored.filter(f =>
                f.path.toLowerCase().includes(searchLow) ||
                label.toLowerCase().includes(searchLow))
            : scored;

          if (searchLow && filtered.length === 0 && !label.toLowerCase().includes(searchLow)) return null;

          const sKey = `node-${node.id}`;
          const isOpen = expanded[sKey] !== false; // default open

          return (
            <div key={node.id} style={{ marginBottom: '3px' }}>
              <div
                onClick={() => toggle(sKey)}
                style={{
                  padding: '5px 6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  borderRadius: '4px',
                  background: 'var(--bg-tertiary)',
                  userSelect: 'none',
                }}
              >
                <span style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>{isOpen ? '▼' : '▶'}</span>
                <span style={{
                  fontSize: '12px',
                  fontWeight: 600,
                  flex: 1,
                  color: 'var(--text-primary)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>{label}</span>
                <button
                  onMouseDown={e => { e.preventDefault(); e.stopPropagation(); onInsert(`{{ nodes['${label}'] }}`); }}
                  title="Insert full output reference"
                  style={{
                    fontSize: '10px',
                    padding: '1px 5px',
                    background: 'var(--bg-input)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '3px',
                    cursor: 'pointer',
                    color: 'var(--text-secondary)',
                    flexShrink: 0,
                  }}
                >full</button>
              </div>

              {isOpen && (
                <div style={{ paddingLeft: '10px' }}>
                  {filtered.length === 0 ? (
                    <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', padding: '4px 6px', fontStyle: 'italic' }}>
                      {ns ? 'No output fields.' : 'Run workflow to see fields.'}
                    </div>
                  ) : (
                    filtered.map(({ path, preview, score }) => (
                      <div
                        key={path}
                        onMouseDown={e => { e.preventDefault(); onInsert(`{{ nodes['${label}'].${path} }}`); }}
                        style={rowStyle}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.1)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <span style={{ fontFamily: 'monospace', fontSize: '11px', color: 'var(--accent-color)', flex: 1 }}>
                          {score > 0 && <span style={{ color: '#4ade80', marginRight: '3px' }}>★</span>}
                          .{path}
                        </span>
                        <span style={{
                          fontSize: '10px',
                          color: 'var(--text-tertiary)',
                          maxWidth: '80px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>{preview}</span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Env vars */}
        {envVars.length > 0 && (() => {
          const filteredEnv = searchLow
            ? envVars.filter(v => v.key.toLowerCase().includes(searchLow))
            : envVars;
          if (searchLow && filteredEnv.length === 0) return null;
          const isOpen = expanded['env'] !== false;
          return (
            <div style={{
              marginTop: '4px',
              borderTop: ancestorNodes.length > 0 ? '1px solid var(--border-color)' : 'none',
              paddingTop: ancestorNodes.length > 0 ? '6px' : 0,
            }}>
              <div
                onClick={() => toggle('env')}
                style={{
                  padding: '5px 6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  borderRadius: '4px',
                  background: 'var(--bg-tertiary)',
                  userSelect: 'none',
                }}
              >
                <span style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>{isOpen ? '▼' : '▶'}</span>
                <span style={{ fontSize: '12px', fontWeight: 600, flex: 1, color: 'var(--text-primary)' }}>env</span>
              </div>
              {isOpen && filteredEnv.map(v => (
                <div
                  key={v.key}
                  onMouseDown={e => { e.preventDefault(); onInsert(`{{ env['${v.key}'] }}`); }}
                  style={{ ...rowStyle, paddingLeft: '16px' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.1)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <span style={{ fontFamily: 'monospace', fontSize: '11px', color: 'var(--accent-color)', flex: 1 }}>{v.key}</span>
                  <span style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>
                    {v.is_encrypted ? '🔒' : String(v.value || '').slice(0, 20)}
                  </span>
                </div>
              ))}
            </div>
          );
        })()}

        {/* Date / Time shortcuts */}
        {(!searchLow || ['now', 'date', 'time', 'datetime'].some(k => k.includes(searchLow))) && (
          <div style={{ marginTop: '4px', borderTop: '1px solid var(--border-color)', paddingTop: '6px' }}>
            <div style={{
              fontSize: '10px',
              fontWeight: 700,
              color: 'var(--text-tertiary)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              padding: '2px 6px',
              marginBottom: '3px',
            }}>Date / Time</div>
            {[
              { ref: '{{ now }}', label: 'now', desc: 'ISO datetime' },
              { ref: "{{ now.strftime('%Y-%m-%d') }}", label: "strftime('%Y-%m-%d')", desc: 'Date only' },
            ].map(t => (
              <div
                key={t.ref}
                onMouseDown={e => { e.preventDefault(); onInsert(t.ref); }}
                style={rowStyle}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.1)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <span style={{ fontFamily: 'monospace', fontSize: '11px', color: 'var(--accent-color)', flex: 1 }}>{t.label}</span>
                <span style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>{t.desc}</span>
              </div>
            ))}
          </div>
        )}

        {/* helpers.* functions */}
        {(() => {
          const visibleCats = HELPERS_CATALOG.map(cat => ({
            ...cat,
            fns: searchLow
              ? cat.fns.filter(f =>
                  f.name.includes(searchLow) ||
                  f.desc.toLowerCase().includes(searchLow) ||
                  cat.category.toLowerCase().includes(searchLow))
              : cat.fns,
          })).filter(cat => cat.fns.length > 0);

          if (visibleCats.length === 0) return null;

          return (
            <div style={{ marginTop: '4px', borderTop: '1px solid var(--border-color)', paddingTop: '6px' }}>
              <div style={{
                fontSize: '10px',
                fontWeight: 700,
                color: 'var(--text-tertiary)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                padding: '2px 6px',
                marginBottom: '3px',
              }}>helpers.*</div>
              {visibleCats.map(cat => (
                <HelperCategorySection
                  key={cat.category}
                  cat={cat}
                  expanded={expanded}
                  toggle={toggle}
                  rowStyle={rowStyle}
                  onInsert={onInsert}
                />
              ))}
            </div>
          );
        })()}

        {/* Plain English AI resolver */}
        {!searchLow && (
          <div style={{ marginTop: '4px', borderTop: '1px solid var(--border-color)', paddingTop: '6px' }}>
            <div
              onClick={() => setShowNL(v => !v)}
              style={{
                padding: '5px 6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                borderRadius: '4px',
                background: 'var(--bg-tertiary)',
                userSelect: 'none',
              }}
            >
              <span style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>{showNL ? '▼' : '▶'}</span>
              <span style={{ fontSize: '11px', fontWeight: 600, color: '#a855f7' }}>Plain English → Expression</span>
            </div>
            {showNL && (
              <div style={{ padding: '8px 4px 4px' }}>
                {nlCredentials.length > 1 && (
                  <select
                    value={nlCredentialId}
                    onChange={e => setNlCredentialId(e.target.value)}
                    style={{ width: '100%', marginBottom: '6px', padding: '4px 6px', background: 'var(--bg-input)', border: '1px solid var(--border-color)', borderRadius: '4px', color: 'var(--text-primary)', fontSize: '12px' }}
                  >
                    <option value="">-- AI credential --</option>
                    {nlCredentials.map(c => <option key={c.id} value={String(c.id)}>{c.name} ({c.service_type})</option>)}
                  </select>
                )}
                {nlCredentials.length === 0 && (
                  <div style={{ fontSize: '11px', color: '#f87171', marginBottom: '6px' }}>No OpenAI/Gemini credential found.</div>
                )}
                <textarea
                  value={nlDescription}
                  onChange={e => setNlDescription(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); resolveNL(); } }}
                  placeholder={`Describe what goes in "${fieldLabel || 'this field'}"...`}
                  style={{ width: '100%', minHeight: '44px', resize: 'none', padding: '6px', background: 'var(--bg-input)', border: '1px solid var(--border-color)', borderRadius: '4px', color: 'var(--text-primary)', fontSize: '12px', boxSizing: 'border-box', marginBottom: '6px' }}
                />
                <button
                  onMouseDown={e => e.preventDefault()}
                  onClick={resolveNL}
                  disabled={nlResolving || !nlDescription.trim() || !nlCredentialId}
                  style={{ padding: '4px 10px', background: '#a855f7', border: 'none', borderRadius: '4px', color: 'white', fontSize: '11px', cursor: 'pointer', opacity: (nlResolving || !nlDescription.trim() || !nlCredentialId) ? 0.5 : 1 }}
                >
                  {nlResolving ? 'Resolving...' : 'Resolve'}
                </button>
                {nlError && <span style={{ fontSize: '11px', color: '#f87171', marginLeft: '6px' }}>{nlError}</span>}
                {nlResult && (
                  <div style={{ marginTop: '6px', padding: '6px 8px', background: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.3)', borderRadius: '4px' }}>
                    <div style={{ fontFamily: 'monospace', fontSize: '11px', color: '#a855f7', wordBreak: 'break-all', marginBottom: '4px' }}>{nlResult}</div>
                    <button
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => { onInsert(nlResult); if (onClose) onClose(); }}
                      style={{ padding: '3px 8px', background: '#a855f7', border: 'none', borderRadius: '4px', color: 'white', fontSize: '11px', cursor: 'pointer' }}
                    >
                      Use this
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ExpressionInput — main export
// ---------------------------------------------------------------------------


// ---------------------------------------------------------------------------
// ExpressionInput — main export
// ---------------------------------------------------------------------------

/**
 * Drop-in replacement for <input type="text"> / <textarea> in node config fields.
 *
 * Features:
 *   - Autocomplete dropdown fires when the cursor enters {{ ... }}
 *   - Unified field picker: upstream fields (★-ranked), env vars, date/time, helpers, AI NL resolver
 *   - Accent border signals an active expression in the value
 *   - Forwards onDrop / onDragOver for drag-to-insert from VariableInspector
 */
export default function ExpressionInput({
  value = '',
  onChange,
  placeholder = '',
  multiline = false,
  ancestorNodes = [],
  nodeStatus = {},
  envVars = [],
  style = {},
  onDrop,
  onDragOver,
  fieldLabel = '',
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionIdx, setSuggestionIdx] = useState(0);
  const [showPicker, setShowPicker] = useState(false);
  const [isPoppedOut, setIsPoppedOut] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!dropdownRef.current || suggestions.length === 0) return;
    const items = dropdownRef.current.querySelectorAll('[data-suggestion]');
    items[suggestionIdx]?.scrollIntoView({ block: 'nearest' });
  }, [suggestionIdx, suggestions.length]);

  const hasExpression = typeof value === 'string' && value.includes('{{');

  // Build node output map for preview resolution
  const nodeOutputMap = useMemo(() => {
    const map = {};
    for (const n of ancestorNodes) {
      const label = n.data?.label || n.id;
      const out = nodeStatus[n.id]?.output;
      if (out !== undefined) map[label] = out;
    }
    return map;
  }, [ancestorNodes, nodeStatus]);

  const envMap = useMemo(() => {
    const map = {};
    for (const v of envVars) map[v.key] = v.is_encrypted ? '🔒' : v.value;
    return map;
  }, [envVars]);

  const preview = useMemo(() => {
    if (!hasExpression) return null;
    return resolveTemplatePreview(value, { nodes: nodeOutputMap, env: envMap });
  }, [hasExpression, value, nodeOutputMap, envMap]);
  const editorMinHeight = isPoppedOut ? 'clamp(260px, 60vh, 760px)' : 'clamp(90px, 20vh, 220px)';
  const previewMaxHeight = isPoppedOut ? 'clamp(90px, 24vh, 260px)' : 'clamp(56px, 13vh, 130px)';

  const baseInputStyle = {
    width: '100%',
    display: 'block',
    padding: '8px',
    border: `1px solid ${hasExpression ? 'var(--accent-color)' : 'var(--border-color)'}`,
    borderRadius: '4px',
    boxSizing: 'border-box',
    fontFamily: hasExpression ? "'Consolas', 'Fira Code', monospace" : 'inherit',
    background: 'var(--bg-input)',
    color: 'var(--text-primary)',
    fontSize: '13px',
    marginBottom: 0,
    outline: 'none',
    ...style,
    ...(multiline ? { minHeight: editorMinHeight, resize: 'vertical' } : {}),
  };

  const handleChange = e => {
    const newVal = e.target.value;
    onChange(newVal);
    const cursor = e.target.selectionStart;
    const partial = getPartialExpression(newVal, cursor);
    const sugs = buildSuggestions(partial, ancestorNodes, nodeStatus, envVars);
    setSuggestions(sugs);
    setSuggestionIdx(0);
  };

  const handleFocus = () => {};

  const handleKeyDown = e => {
    if (suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSuggestionIdx(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSuggestionIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      const s = suggestions[suggestionIdx];
      if (s && !s.isCategory && s.insert) {
        e.preventDefault();
        applySuggestion(s);
      }
    } else if (e.key === 'Escape') {
      setSuggestions([]);
    }
  };

  const applySuggestion = useCallback((suggestion) => {
    if (!suggestion.insert) return;
    const el = inputRef.current;
    if (!el) return;
    const cursor = el.selectionStart;
    const currentValue = el.value;
    const exprStart = findExpressionStart(currentValue, cursor);
    if (exprStart === -1) return;

    const before = currentValue.slice(0, exprStart);
    const rest = currentValue.slice(cursor);
    // Strip any trailing whitespace + }} from the remainder
    const afterExpr = rest.replace(/^\s*\}\}/, '');
    const newVal = `${before}{{ ${suggestion.insert}${afterExpr}`;
    onChange(newVal);
    setSuggestions([]);
    // Move cursor after the inserted expression
    const newCursor = before.length + 3 + suggestion.insert.length;
    setTimeout(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = newCursor;
    }, 0);
  }, [onChange]);

  const insertReference = useCallback((ref) => {
    const el = inputRef.current;
    if (!el) return;
    const currentValue = el.value;
    const cursor = el.selectionStart ?? currentValue.length;
    const newVal = `${currentValue.slice(0, cursor)}${ref}${currentValue.slice(cursor)}`;
    onChange(newVal);
    setShowPicker(false);
    setTimeout(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = cursor + ref.length;
    }, 0);
  }, [onChange]);

  // Close popups on outside click
  useEffect(() => {
    const handler = e => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setSuggestions([]);
        setShowPicker(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsPoppedOut(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    if (!isPoppedOut) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isPoppedOut]);

  const handleCursorChange = e => {
    if (['ArrowUp', 'ArrowDown', 'Enter', 'Tab', 'Escape'].includes(e.key)) return;
    const cursor = e.target.selectionStart;
    const partial = getPartialExpression(e.target.value, cursor);
    const sugs = buildSuggestions(partial, ancestorNodes, nodeStatus, envVars);
    setSuggestions(sugs);
    setSuggestionIdx(0);
  };

  const InputEl = multiline ? 'textarea' : 'input';

  return (
    <>
      {isPoppedOut && (
        <div
          onMouseDown={() => setIsPoppedOut(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.55)',
            zIndex: 1990,
          }}
        />
      )}
      <div
        ref={containerRef}
        style={isPoppedOut ? {
          position: 'fixed',
          top: '5vh',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'min(1000px, 96vw)',
          maxHeight: '90vh',
          overflowY: 'auto',
          marginBottom: 0,
          zIndex: 2000,
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border-color)',
          borderRadius: '10px',
          boxShadow: '0 16px 48px rgba(0,0,0,0.5)',
          padding: '10px',
        } : { position: 'relative', marginBottom: '15px', ...style }}
      >
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '6px' }}>
        <div style={{ display: 'flex', flexShrink: 0, border: '1px solid var(--border-color)', borderRadius: '4px', overflow: 'hidden' }}>
          <button
            title={isPoppedOut ? 'Collapse editor' : 'Expand editor'}
            onMouseDown={e => e.preventDefault()}
            onClick={() => setIsPoppedOut(v => !v)}
            style={{ padding: '6px 8px', background: isPoppedOut ? 'rgba(99,102,241,0.15)' : 'transparent', border: 'none', borderRight: '1px solid var(--border-color)', cursor: 'pointer', color: isPoppedOut ? 'var(--accent-color)' : 'var(--text-tertiary)', fontSize: '12px', lineHeight: 1, userSelect: 'none' }}
          >{isPoppedOut ? '⤡' : '⤢'}</button>
          <button
            title="Insert field reference"
            onMouseDown={e => e.preventDefault()}
            onClick={() => { setShowPicker(p => !p); setSuggestions([]); }}
            style={{ padding: '6px 8px', background: showPicker ? 'rgba(99,102,241,0.15)' : 'transparent', border: 'none', cursor: 'pointer', color: showPicker ? 'var(--accent-color)' : 'var(--text-tertiary)', fontSize: '11px', fontFamily: 'monospace', fontWeight: 700, lineHeight: 1, userSelect: 'none' }}
          >{'{}'}</button>
        </div>
      </div>
      <InputEl
        ref={inputRef}
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        onClick={handleCursorChange}
        onKeyUp={handleCursorChange}
        onDrop={onDrop}
        onDragOver={onDragOver}
        placeholder={placeholder}
        style={baseInputStyle}
        type={InputEl === 'input' ? 'text' : undefined}
      />

      {/* Autocomplete dropdown */}
      {suggestions.length > 0 && (
        <div ref={dropdownRef} style={{
          position: 'absolute',
          top: 'calc(100% + 2px)',
          left: 0,
          right: 0,
          background: 'var(--bg-secondary)',
          border: '1px solid var(--accent-color)',
          borderRadius: '4px',
          zIndex: 1100,
          maxHeight: '220px',
          overflowY: 'auto',
          boxShadow: '0 4px 14px rgba(0,0,0,0.35)',
        }}>
          {suggestions.map((s, i) => (
            <div
              key={s.label + i}
              data-suggestion
              onMouseDown={e => { e.preventDefault(); if (!s.isCategory && s.insert) applySuggestion(s); }}
              style={{
                padding: '6px 10px',
                cursor: s.isCategory ? 'default' : 'pointer',
                background: i === suggestionIdx && !s.isCategory ? 'rgba(99,102,241,0.15)' : 'transparent',
                borderBottom: i < suggestions.length - 1 ? '1px solid var(--border-color)' : 'none',
              }}
              onMouseEnter={() => !s.isCategory && setSuggestionIdx(i)}
            >
              <div style={{
                fontFamily: 'monospace',
                fontSize: '12px',
                color: s.isCategory ? 'var(--text-tertiary)' : 'var(--accent-color)',
                fontWeight: s.isCategory ? 400 : 500,
              }}>{s.label}</div>
              {s.desc && (
                <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginTop: '1px' }}>{s.desc}</div>
              )}
            </div>
          ))}
          <div style={{
            padding: '4px 8px',
            fontSize: '10px',
            color: 'var(--text-tertiary)',
            borderTop: '1px solid var(--border-color)',
            fontStyle: 'italic',
          }}>
            ↑↓ navigate · Enter/Tab insert · Esc dismiss
          </div>
        </div>
      )}

      {/* Unified field picker panel */}
      {showPicker && (
        <FieldPickerPanel
          ancestorNodes={ancestorNodes}
          nodeStatus={nodeStatus}
          envVars={envVars}
          fieldLabel={fieldLabel}
          onInsert={insertReference}
          onClose={() => setShowPicker(false)}
        />
      )}

      {/* Expression preview strip */}
      {preview && !showPicker && suggestions.length === 0 && (
        <div style={{
          marginTop: '3px',
          padding: '4px 8px',
          background: preview.ok ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
          border: `1px solid ${preview.ok ? 'rgba(34,197,94,0.25)' : 'rgba(239,68,68,0.25)'}`,
          borderRadius: '4px',
          fontSize: '11px',
          fontFamily: "'Consolas', 'Fira Code', monospace",
          color: preview.ok ? 'var(--text-secondary)' : '#f87171',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '6px',
          wordBreak: 'break-all',
        }}>
          <span style={{ color: preview.ok ? '#4ade80' : '#f87171', flexShrink: 0, marginTop: '1px' }}>
            {preview.ok ? '▶' : '!'}
          </span>
          <span style={{ flex: 1, maxHeight: previewMaxHeight, overflowY: 'auto' }}>
            {preview.resolved === '' ? <em style={{ opacity: 0.5 }}>(empty string)</em> : preview.resolved}
          </span>
        </div>
      )}
      </div>
    </>
  );
}
